import { useCallback, useEffect, useState } from 'react';
import type { DocBlock } from '@itsappy/shared';
import { wordCount } from '@itsappy/shared';
import { useSocket } from '@/realtime/socketContext';
import { API_URL } from '@/lib/config';
import { tokenStore } from '@/lib/api';
import { toast } from '@/store/ui';
import { Dropdown, MenuItem, Spinner } from '@/components/ui';
import { DownloadIcon } from '@/components/ui/icons';
import { cn } from '@/lib/utils';
import { DocEditor } from './DocEditor';

/**
 * A whole document: the socket subscription, the editor, and the export menu.
 *
 * The paragraphs come from `doc:subscribe` rather than from the REST payload,
 * so being on screen and being subscribed to other people's edits are the
 * same moment — there is no window in which the page is visible but deaf.
 */
export function DocSurface({
  docId,
  name,
  className,
}: {
  docId: string;
  name: string;
  className?: string;
}) {
  const { socket } = useSocket();

  const [blocks, setBlocks] = useState<DocBlock[] | null>(null);
  const [canEdit, setCanEdit] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!socket) return;
    let cancelled = false;
    setBlocks(null);
    setError(null);

    socket.emit('doc:subscribe', { docId }, (ack) => {
      if (cancelled) return;
      if (!ack.ok) {
        setError(ack.error ?? 'This document could not be opened');
        return;
      }
      setBlocks(ack.blocks ?? []);
      setCanEdit(Boolean(ack.canEdit));
    });

    return () => {
      cancelled = true;
      socket.emit('doc:unsubscribe', { docId });
    };
  }, [socket, docId]);

  /**
   * Keeps this component's copy in step with the editor's, so the word count
   * below tracks what is on screen rather than what arrived at subscribe time.
   */
  const handleBlocksChanged = useCallback((next: DocBlock[]) => setBlocks(next), []);

  /**
   * Downloads the live document. The export is an authenticated route, so it
   * is fetched and handed over as a blob — a bare `<a href>` would arrive
   * without the token and come back a 401.
   */
  const download = async (format: 'docx' | 'md') => {
    setExporting(true);
    try {
      const response = await fetch(
        `${API_URL}/docs/${docId}/export${format === 'md' ? '?format=md' : ''}`,
        {
          headers: {
            Authorization: `Bearer ${tokenStore.access ?? ''}`,
            ...(tokenStore.orgId ? { 'X-Org-Id': tokenStore.orgId } : {}),
          },
        },
      );
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${name}.${format}`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('That document could not be exported');
    } finally {
      setExporting(false);
    }
  };

  if (error) {
    return (
      <div className="grid h-full place-items-center px-4 text-center">
        <p className="text-xs text-slate-500">{error}</p>
      </div>
    );
  }

  if (!blocks) {
    return (
      <div className="grid h-full place-items-center">
        <Spinner />
      </div>
    );
  }

  const words = wordCount(blocks);

  return (
    <div className={cn('flex min-h-0 flex-col', className)}>
      <DocEditor
        docId={docId}
        blocks={blocks}
        canEdit={canEdit}
        onBlocksChanged={handleBlocksChanged}
        className="min-h-0 flex-1"
      />

      <div className="flex items-center gap-3 border-t border-surface-3 bg-surface-2 px-3 py-1.5 text-[11px] text-slate-400">
        <span>
          {words} {words === 1 ? 'word' : 'words'}
        </span>
        {!canEdit && <span>Read only</span>}

        <Dropdown
          className="ml-auto"
          trigger={
            <span className="flex items-center gap-1 rounded-md px-2 py-1 font-medium text-slate-400 transition-colors hover:bg-surface-3 hover:text-ink">
              {exporting ? <Spinner className="h-3 w-3" /> : <DownloadIcon className="h-3.5 w-3.5" />}
              Export
            </span>
          }
        >
          <MenuItem onClick={() => void download('docx')}>Word (.docx)</MenuItem>
          <MenuItem onClick={() => void download('md')}>Markdown (.md)</MenuItem>
        </Dropdown>
      </div>
    </div>
  );
}
